import React, { useState, useEffect, useRef } from 'react';
import useOpenFromQuery from '../../../hooks/useOpenFromQuery';
import { toast } from 'react-hot-toast';
import { PlusIcon, EyeIcon, CheckCircleIcon, XCircleIcon, TrashIcon, MagnifyingGlassIcon, PrinterIcon, PencilIcon, NoSymbolIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import DebitNoteForm from './DebitNoteForm';
import DebitNoteView from './DebitNoteView';
import DebitNoteFromReturns from './DebitNoteFromReturns';
import DebitNoteBulkEntry from './DebitNoteBulkEntry';
import DebitNoteBulkFromGRNs from './DebitNoteBulkFromGRNs';
import { openDebitNoteReport } from '../../../utils/openInventoryReport';
import { canPrintDebitNote } from '../../../utils/documentPrintGate';

const statusColors = {
  pending_approval: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200'
};

const DebitNoteManagement = () => {
  const { hasPermission } = useAuth();
  const [debitNotes, setDebitNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[DebitNoteManagement] could not load location info:', e.message);
      }
    })();
  }, []);

  const handlePrint = async (dn) => {
    await openDebitNoteReport(dn, companyInfo);
  };
  const searchTimeoutRef = useRef(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const [showForm, setShowForm] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [showFromReturns, setShowFromReturns] = useState(false);
  const [showBulkEntry, setShowBulkEntry] = useState(false);
  const [showBulkFromGRNs, setShowBulkFromGRNs] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  const [viewId, setViewId] = useState(null);
  // opened from a link elsewhere (e.g. Vendor History) with ?view=<debit note id>
  useOpenFromQuery((id) => setViewId(id));
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmComment, setConfirmComment] = useState('');

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchInput]);

  useEffect(() => {
    fetchDebitNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, sourceFilter, pagination.page, pagination.limit]);

  const fetchDebitNotes = async () => {
    try {
      setLoading(true);
      const res = await inventoryApi.getDebitNotes({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch,
        status: statusFilter,
        source: sourceFilter
      });
      setDebitNotes(res.data || []);
      setPagination(prev => ({ ...prev, ...(res.pagination || {}) }));
    } catch (err) {
      console.error('Error fetching debit notes:', err);
      toast.error('Failed to load debit notes');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => parseFloat(n || 0).toFixed(2);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  const handleApprove = (dn) => setConfirmAction({
    type: 'info', title: 'Approve Debit Note',
    message: `Approve debit note "${dn.debitNoteNumber}" for ${fmt(dn.totals?.totalWithTax)}? This posts a GL entry and opens it for setoff.`,
    confirmText: 'Approve', showCommentBox: true, commentPlaceholder: 'Approval notes (optional)...',
    onConfirm: async () => {
      try {
        setConfirmLoading(true);
        await inventoryApi.approveDebitNote(dn._id, { comments: confirmComment.trim() || undefined });
        toast.success('Debit note approved'); fetchDebitNotes(); setConfirmAction(null); setConfirmComment('');
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to approve'); }
      finally { setConfirmLoading(false); }
    }
  });

  const handleReject = (dn) => setConfirmAction({
    type: 'warning', title: 'Reject Debit Note', message: `Reject debit note "${dn.debitNoteNumber}"?`,
    confirmText: 'Reject', showCommentBox: true, commentPlaceholder: 'Rejection reason (required)...',
    onConfirm: async () => {
      if (!confirmComment?.trim()) { toast.error('Rejection reason is required'); return; }
      try {
        setConfirmLoading(true);
        await inventoryApi.rejectDebitNote(dn._id, { reason: confirmComment.trim() });
        toast.success('Debit note rejected'); fetchDebitNotes(); setConfirmAction(null); setConfirmComment('');
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to reject'); }
      finally { setConfirmLoading(false); }
    }
  });

  const handleCancel = (dn) => setConfirmAction({
    type: 'warning', title: 'Cancel Debit Note',
    message: dn.status === 'approved'
      ? `Cancel debit note "${dn.debitNoteNumber}" (${fmt(dn.totals?.totalWithTax)})? Its GL entry is reversed and the budget it gave back is used again.`
      : `Cancel debit note "${dn.debitNoteNumber}"?`,
    confirmText: 'Cancel Debit Note', showCommentBox: true, commentPlaceholder: 'Reason (optional)...',
    onConfirm: async () => {
      try {
        setConfirmLoading(true);
        const res = await inventoryApi.cancelDebitNote(dn._id, { reason: confirmComment.trim() || undefined });
        toast.success(res?.message || 'Debit note cancelled'); fetchDebitNotes(); setConfirmAction(null); setConfirmComment('');
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to cancel'); }
      finally { setConfirmLoading(false); }
    }
  });

  const handleRepost = async (dn) => {
    try {
      const res = await inventoryApi.repostDebitNoteGl(dn._id);
      toast.success(res?.message || 'GL entry posted');
    } catch (err) { toast.error(err.response?.data?.message || 'GL entry not posted'); }
    fetchDebitNotes();
  };

  const isManual = (dn) => dn.source !== 'SUPPLIER_RETURN';
  const glBadge = (dn) => {
    if (!isManual(dn)) return null;
    if (dn.status === 'cancelled' && dn.glReversal?.docCode) return <span className="text-[10px] text-gray-500">GL reversed</span>;
    if (!['approved', 'completed'].includes(dn.status)) return null;
    const st = dn.glTransaction?.status || 'not posted';
    const cls = st === 'posted' ? 'text-green-700' : st === 'pending' ? 'text-gray-500' : 'text-red-600';
    return <span className={`text-[10px] font-medium ${cls}`} title={dn.glTransaction?.message || ''}>GL {st}</span>;
  };

  const handleDelete = (dn) => setConfirmAction({
    type: 'danger', title: 'Delete Debit Note', message: `Delete debit note "${dn.debitNoteNumber}"? This cannot be undone.`,
    confirmText: 'Delete',
    onConfirm: async () => {
      try {
        setConfirmLoading(true);
        await inventoryApi.deleteDebitNote(dn._id);
        toast.success('Debit note deleted'); fetchDebitNotes(); setConfirmAction(null);
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
      finally { setConfirmLoading(false); }
    }
  });

  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Debit Notes</h1>
            <p className="text-amber-100 text-sm">Raise debit notes against suppliers and set them off in payments</p>
          </div>
          {hasPermission(['INVENTORY:DEBIT_NOTES:CREATE']) && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowFromReturns(true)}
                className="inline-flex items-center px-3 py-1.5 bg-white text-amber-700 hover:bg-amber-50 text-sm font-medium rounded-md">
                <PlusIcon className="w-4 h-4 mr-1.5" /> From Supplier Returns
              </button>
              <button onClick={() => setShowBulkFromGRNs(true)} title="Pick GRNs, apply one rule — one note per supplier"
                className="inline-flex items-center px-3 py-1.5 bg-white text-amber-700 hover:bg-amber-50 text-sm font-medium rounded-md">
                <PlusIcon className="w-4 h-4 mr-1.5" /> Bulk from GRNs
              </button>
              <button onClick={() => setShowBulkEntry(true)} title="Penalties / rebates for many suppliers in one grid"
                className="inline-flex items-center px-3 py-1.5 bg-white text-amber-700 hover:bg-amber-50 text-sm font-medium rounded-md">
                <PlusIcon className="w-4 h-4 mr-1.5" /> Bulk Entry
              </button>
              <button onClick={() => { setEditNote(null); setShowForm(true); }}
                className="inline-flex items-center px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-md">
                <PlusIcon className="w-4 h-4 mr-1.5" /> New Debit Note
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="px-4 py-2 border-b border-gray-200 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input type="text" placeholder="Search by debit note number or supplier..."
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="">All Status</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="">All Sources</option>
            <option value="SUPPLIER_RETURN">From Supplier Returns</option>
            <option value="MANUAL">Manual</option>
          </select>
        </div>

        {/* Table */}
        {loading && debitNotes.length === 0 ? <LoadingSpinner /> : (
          <div className="mx-4 sm:mx-6 lg:mx-8 my-4 overflow-x-auto rounded-sm border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Debit Note #</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Supplier</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Value</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Remaining</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {debitNotes.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-500">No debit notes found</td></tr>
                ) : debitNotes.map(dn => (
                  <tr key={dn._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{dn.debitNoteNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{dn.supplierName}</td>
                    <td className="px-4 py-3 text-xs">
                      {isManual(dn)
                        ? <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{{ PENALTY: 'Penalty', REBATE: 'Rebate' }[dn.noteType] || 'Adjustment'}</span>
                        : <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" title="Created by the supplier return — its GL was posted by the return">Return {dn.supplierReturnNumber}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{fmt(dn.totals?.totalWithTax)}</td>
                    <td className="px-4 py-3 text-sm text-right text-orange-600">{fmt(dn.paymentSetoff?.remainingAmount)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full border ${statusColors[dn.status] || 'bg-gray-100 text-gray-800'}`}>
                        {dn.status?.replace('_', ' ')}
                      </span>
                      <div>{glBadge(dn)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(dn.debitDate)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setViewId(dn._id)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="View">
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {canPrintDebitNote(dn.status) && (
                          <button onClick={() => handlePrint(dn)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Print PDF">
                            <PrinterIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isManual(dn) && hasPermission(['INVENTORY:DEBIT_NOTES:UPDATE']) && dn.status === 'pending_approval' && (
                          <button onClick={() => { setEditNote(dn); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit">
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isManual(dn) && hasPermission(['INVENTORY:DEBIT_NOTES:APPROVE']) && ['approved', 'completed'].includes(dn.status) && ['failed', 'skipped'].includes(dn.glTransaction?.status || 'failed') && !dn.glTransaction?.transactionId && dn.glTransaction?.status !== 'pending' && (
                          <button onClick={() => handleRepost(dn)} className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded" title={`Post GL entry again${dn.glTransaction?.message ? ` — last: ${dn.glTransaction.message}` : ''}`}>
                            <ArrowPathIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isManual(dn) && hasPermission(['INVENTORY:DEBIT_NOTES:APPROVE']) && dn.status === 'pending_approval' && (
                          <>
                            <button onClick={() => handleApprove(dn)} className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded" title="Approve">
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleReject(dn)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded" title="Reject">
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {isManual(dn) && hasPermission(['INVENTORY:DEBIT_NOTES:DELETE']) && dn.status === 'approved' && !(dn.paymentSetoff?.setoffAmount > 0) && (
                          <button onClick={() => handleCancel(dn)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Cancel (reverses the GL entry)">
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isManual(dn) && hasPermission(['INVENTORY:DEBIT_NOTES:DELETE']) && dn.status === 'pending_approval' && (
                          <button onClick={() => handleDelete(dn)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Simple pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">Page {pagination.page} of {pagination.pages} ({pagination.total} total)</span>
            <div className="flex gap-2">
              <button disabled={pagination.page <= 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Previous</button>
              <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <DebitNoteForm
          debitNote={editNote}
          onClose={() => { setShowForm(false); setEditNote(null); }}
          onSuccess={() => { setShowForm(false); setEditNote(null); fetchDebitNotes(); }}
        />
      )}
      {showFromReturns && (
        <DebitNoteFromReturns
          onClose={() => setShowFromReturns(false)}
          onSuccess={() => { setShowFromReturns(false); fetchDebitNotes(); }}
        />
      )}
      {showBulkEntry && (
        <DebitNoteBulkEntry
          onClose={() => setShowBulkEntry(false)}
          onSuccess={() => { setShowBulkEntry(false); fetchDebitNotes(); }}
        />
      )}
      {showBulkFromGRNs && (
        <DebitNoteBulkFromGRNs
          onClose={() => setShowBulkFromGRNs(false)}
          onSuccess={() => { setShowBulkFromGRNs(false); fetchDebitNotes(); }}
        />
      )}
      {viewId && (
        <DebitNoteView debitNoteId={viewId} onClose={() => setViewId(null)} />
      )}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => { setConfirmAction(null); setConfirmLoading(false); setConfirmComment(''); }}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          type={confirmAction.type}
          loading={confirmLoading}
          showCommentBox={confirmAction.showCommentBox}
          comment={confirmComment}
          onCommentChange={setConfirmComment}
          commentPlaceholder={confirmAction.commentPlaceholder}
        />
      )}
    </div>
  );
};

export default DebitNoteManagement;
